<template>
  <main class="m-2 flex min-h-0 flex-1 flex-col overflow-hidden rounded border border-gray-200 bg-white">
    <div class="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-2">
      <section
        class="flex min-h-[28rem] flex-col border-b border-gray-200 lg:min-h-0 lg:border-b-0 lg:border-r"
        aria-label="Aircraft search results"
      >
        <header class="border-b border-gray-200 px-4 py-4">
          <h1 class="text-lg font-bold text-primary">Aircraft TCDS Finder</h1>
          <form class="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end" @submit.prevent="search">
            <Input
            v-model="query"
            class="min-w-0 flex-1"
            label="Aircraft, manufacturer, type or TCDS reference"
            type="search"
            placeholder="A330-243, Airbus, EASA.A.004..."
            autocomplete="off"
            />
            <button type="submit" class="primary !m-0" :disabled="loading || query.trim().length < 2">
              {{ loading ? "Searching..." : "Search" }}
            </button>
          </form>
          <p v-if="error" class="mt-2 text-xs text-red-700">{{ error }}</p>
        </header>

        <div v-if="!searched" class="p-6 text-sm text-gray-500">Search for an aircraft to find its TCDS.</div>
        <div v-else-if="!loading && !results.length" class="p-6 text-sm text-gray-500">No aircraft found.</div>
        <div v-else class="min-h-[20rem] flex-1">
          <DataTable
            :items="results"
            :paginationAutoPageSize="true"
            :tableSettings="AIRCRAFT_TABLE_SETTINGS"
            @itemSelected="onItemSelected"
          />
        </div>
      </section>

      <section class="min-h-0 overflow-y-auto" aria-label="Selected aircraft TCDS details">
        <div v-if="selected" class="p-2">
          <Fields
            title="Selected aircraft"
            :contextData="{ item: selected }"
            :dataFields="DETAIL_FIELDS"
            :hideBackButton="true"
            :menuActions="[]"
          >
            <template #topRight>
            <a
              v-if="selected.tcds_link"
              :href="selected.tcds_link"
              target="_blank"
              rel="noopener noreferrer"
              class="primary inline-flex items-center gap-2 rounded px-3 py-2 text-sm font-bold text-primary"
            >
              <font-awesome-icon icon="external-link-alt" />
              Open TCDS
            </a>
            <span v-else class="rounded border border-gray-200 px-3 py-2 text-xs text-gray-500">TCDS link unavailable</span>
            </template>
          </Fields>
        </div>
        <div v-else class="flex min-h-64 items-center justify-center p-6 text-sm text-gray-500">
          Select an aircraft from the search results.
        </div>

        <AircraftAdmin
          v-if="isAdmin"
          :selected="selected"
          @deleted="onDeleted"
          @imported="search"
          @saved="onSaved"
        />
      </section>
    </div>
  </main>
</template>

<script setup>
import { DataTable, Input } from "@comp/index.js";
import Fields from "@@/components/Fields/Fields.vue";
import { computed, ref } from "vue";
import { useStore } from "vuex";

import { searchAircraft } from "../api.js";
import AircraftAdmin from "./AircraftAdmin.vue";

const AIRCRAFT_TABLE_SETTINGS = {
  columnState: [
      { field: "model", headerName: "Model", flex: 1, minWidth: 120 },
      { field: "variant", headerName: "Variant", flex: 1, minWidth: 120 },
    { field: "manufacturer", headerName: "Manufacturer", flex: 1, minWidth: 150 },
    { field: "ac_type", headerName: "Aircraft type", minWidth: 130 },
    { field: "tcds_ref", headerName: "TCDS reference", minWidth: 140 },
  ],
};

const DETAIL_FIELDS = [
  { key: "manufacturer", label: "Manufacturer" },
  { key: "model", label: "Model" },
  { key: "variant", label: "Variant" },
  { key: "ac_type", label: "Aircraft type" },
  { key: "cs", label: "Certification specification" },
  { key: "country", label: "Country" },
  { key: "tcds_ref", label: "TCDS reference" },
  { key: "tcds_link", label: "TCDS link" },
];

const store = useStore();
const query = ref("");
const results = ref([]);
const selected = ref(null);
const loading = ref(false);
const searched = ref(false);
const error = ref("");
let requestId = 0;

const isAdmin = computed(() => store.state.user?.Security?.General?.ApplicationAdmin === true);

function onItemSelected(event) {
  selected.value = event.data;
}

async function search() {
  const normalizedQuery = query.value.trim();
  if (normalizedQuery.length < 2) return;
  const currentRequest = ++requestId;
  loading.value = true;
  searched.value = true;
  error.value = "";
  try {
    const response = await searchAircraft({ query: normalizedQuery });
    if (currentRequest !== requestId) return;
    results.value = response.data.results || [];
    if (selected.value) {
      selected.value = results.value.find((item) => item._id === selected.value._id) || null;
    }
  } catch (searchError) {
    if (currentRequest !== requestId) return;
    results.value = [];
    error.value = searchError?.message || "Unable to search aircraft.";
  } finally {
    if (currentRequest === requestId) loading.value = false;
  }
}

function onSaved(aircraft) {
  const index = results.value.findIndex((item) => item._id === aircraft._id);
  if (index === -1) results.value.unshift(aircraft);
  else results.value.splice(index, 1, aircraft);
  selected.value = aircraft;
}

function onDeleted(id) {
  results.value = results.value.filter((aircraft) => aircraft._id !== id);
  selected.value = null;
}
</script>
